/**
 * ColumnCollationFields — pure presentational form section for the per-column
 * collation / character-set surface (DSE2 §3.A4 / Slice A4).
 *
 *  • Collation name input (verbatim text — opaque to the resolver). Per
 *    engine the legal vocabulary differs (mssql `Latin1_General_CI_AS`,
 *    pgsql `"en-US-x-icu"`, mysql `utf8mb4_unicode_ci`, oracle `BINARY_CI`,
 *    sqlite `BINARY|NOCASE|RTRIM`). The form keeps the field free-text and
 *    lets the synthesizer/engine validate.
 *  • Character set input (mysql-only) — the form gates the input via
 *    `isCharsetSupported` and surfaces the documented tooltip on every other
 *    engine via `getCharsetUnsupportedTooltip`.
 *  • Per-engine version note (oracle 12.2+ / `MAX_STRING_SIZE = EXTENDED`)
 *    via `getCollationEngineVersionNote`.
 *
 * State is owned by the parent.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice A4.
 */
import type { SqlEngine } from '../../../../sql-grammar/model';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import {
  getCharsetUnsupportedTooltip,
  getCollationEngineVersionNote,
  isCharsetSupported,
} from '../../edit/edits/setColumnCollation';

export interface ColumnCollationFieldsProps {
  readonly engine: SqlEngine;
  readonly testId: string;
  readonly name: string;
  readonly setName: (next: string) => void;
  readonly charset: string;
  readonly setCharset: (next: string) => void;
  /** True when the section is mutually exclusive with another form section
   *  (none today — reserved for future predicate composition). */
  readonly disabled?: boolean;
}

export function ColumnCollationFields(props: ColumnCollationFieldsProps) {
  const { engine, testId, name, setName, charset, setCharset, disabled } = props;
  const charsetEnabled = isCharsetSupported(engine);
  const charsetTooltip = charsetEnabled ? undefined : getCharsetUnsupportedTooltip(engine);
  const versionNote = getCollationEngineVersionNote(engine);

  return (
    <fieldset
      className="flex flex-col gap-1"
      data-testid={`${testId}-section`}
      disabled={disabled}
    >
      <legend className="text-xs">Collation (optional)</legend>
      <Label htmlFor={`${testId}-name`} className="text-[10px] text-muted-foreground">
        Collation name
      </Label>
      <Input
        id={`${testId}-name`}
        data-testid={`${testId}-name`}
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={collationPlaceholder(engine)}
        className="h-8 font-mono text-xs"
      />
      <Label htmlFor={`${testId}-charset`} className="text-[10px] text-muted-foreground">
        Character set (MySQL)
      </Label>
      <Input
        id={`${testId}-charset`}
        data-testid={`${testId}-charset`}
        value={charset}
        disabled={!charsetEnabled || disabled}
        title={charsetTooltip}
        onChange={(e) => setCharset(e.target.value)}
        placeholder={charsetEnabled ? 'utf8mb4' : ''}
        className="h-8 font-mono text-xs disabled:opacity-60"
      />
      {versionNote ? (
        <span
          className="text-[10px] text-muted-foreground"
          data-testid={`${testId}-version-note`}
        >
          {versionNote}
        </span>
      ) : null}
    </fieldset>
  );
}

function collationPlaceholder(engine: SqlEngine): string {
  if (engine === 'mssql') return 'Latin1_General_CI_AS';
  if (engine === 'postgres') return 'en-US-x-icu';
  if (engine === 'mysql') return 'utf8mb4_unicode_ci';
  if (engine === 'oracle') return 'BINARY_CI';
  return 'NOCASE';
}
